import { useCallback, useId, useState } from 'react';
import { SurfacePagePanel } from '@/design-system';
import { SURFACE_PAGE_TAB_ACTIVE, SURFACE_PAGE_TAB_INACTIVE } from '@/shared/ui-class';
import { cn } from '@/shared/cn';
import type { SessionState } from '@/versions/v1/schema/scenario';
import { OperatingCurve, ZoneMarker } from '@/versions/v1/modules/_viz/OperatingCurve';
import { vizToneColor } from '@/versions/v1/modules/_viz/viz-tone';
import { GmCard } from './GmTabs';
import {
  buildLedgerModel,
  projectOperatingCurvePanel,
  type Metal,
} from './OperatingCurvePanel.transform';

type OperatingCurvePanelProps = {
  state: SessionState;
  /** `met` mounts on the Metallurgist landing: the future/what-if point is
   * omitted (the record, not the decision) and the ledger gains the
   * `7 d · N of M on the pair` line (Q5–Q6). Default is the Superintendent
   * mount — unchanged. */
  variant?: 'default' | 'met';
  className?: string;
};

const METALS: Metal[] = ['Zn', 'Pb', 'Ag'];

export function OperatingCurvePanel({ state, variant = 'default', className }: OperatingCurvePanelProps) {
  const isMet = variant === 'met';
  const { curves } = projectOperatingCurvePanel(state, { omitFuture: isMet });
  const [metal, setMetal] = useState<Metal>('Zn');
  const curve = curves.find((row) => row.metal === metal) ?? curves[0]!;
  const ledger = buildLedgerModel(curves, metal, { showSevenDayRow: isMet });
  const chartPanelId = useId();
  const tabId = useCallback((m: Metal) => `operating-curve-tab-${m}`, []);

  const focusTab = (next: Metal) => {
    setMetal(next);
    document.getElementById(tabId(next))?.focus();
  };

  const onTabKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>, index: number) => {
    let nextIndex = index;
    if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
      event.preventDefault();
      nextIndex = (index + 1) % METALS.length;
    } else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
      event.preventDefault();
      nextIndex = (index - 1 + METALS.length) % METALS.length;
    } else {
      return;
    }
    focusTab(METALS[nextIndex]!);
  };

  const ledgerLine = (row: { label: string; grade: string; recovery: string }) =>
    `${row.label} · ${row.grade} · ${row.recovery}`;

  if (isMet) {
    // The Control metallurgist's 7-day operating record (polish.P26 #713,
    // locked comp `landing-control-met.html` `opCard`): a card in the landing
    // family, the metal switch under the title, the plot, then the ledger as
    // hairline rows with the pair right-aligned and the verdict in ink.
    return (
      <GmCard
        title="Operating point · grade vs recovery"
        meta={<span data-operating-record-meta>7-day record</span>}
        className={className}
        data-operating-record
      >
        <div
          role="tablist"
          aria-label="Concentrate metal"
          className="mb-1 inline-flex gap-0.5 rounded-full border border-[var(--hairline-strong)] bg-[var(--surface-1)] p-0.5"
        >
          {ledger.tabItems.map((item, index) => {
            const active = item.id === metal;
            return (
              <button
                key={item.id}
                id={tabId(item.id)}
                type="button"
                role="tab"
                aria-selected={active}
                aria-controls={chartPanelId}
                tabIndex={active ? 0 : -1}
                onClick={() => setMetal(item.id)}
                onKeyDown={(event) => onTabKeyDown(event, index)}
                className={cn(
                  'rounded-full px-2.5 py-0.5 text-xs font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-accent-500)]',
                  active ? 'bg-ink-primary text-[var(--surface-1)]' : 'text-ink-secondary hover:text-ink-primary',
                )}
              >
                {item.label}
              </button>
            );
          })}
        </div>
        <div id={chartPanelId} role="tabpanel" aria-labelledby={tabId(metal)} className="min-w-0">
          <OperatingCurve
            xLabel={curve.xLabel}
            yLabel={curve.yLabel}
            xUnit={curve.xUnit}
            yUnit={curve.yUnit}
            design={curve.design}
            trail={curve.trail}
            current={curve.current}
            future={curve.future}
            tone={curve.tone}
            // The comp's record is a taller plot with a unit-step recovery
            // axis and units in the axis titles only (polish.P26 r1); 222 px
            // puts the labelled gridlines 156 px apart, as the comp's do.
            height={222}
            metRecoveryAxis
            cursorTooltip={curve.current.label}
            xDomain={curve.xDomain}
            yDomain={curve.yDomain}
          />
        </div>
        <div className="flex flex-col text-[12.5px] tabular-nums" data-operating-record-ledger>
          {[ledger.designRow, ledger.nowRow].map((row) => (
            <p
              key={row.label}
              className="flex justify-between gap-2.5 border-b border-[var(--color-divider)] py-[5px] text-ink-secondary"
              title={row.tooltip}
            >
              <span>{row.label}</span>
              <span className="font-semibold text-ink-primary">
                {row.grade} · {row.recovery}
              </span>
            </p>
          ))}
          {ledger.zoneRows.map((row) => (
            <p
              key={row.label}
              className="flex items-center gap-1.5 border-b border-[var(--color-divider)] py-[5px] text-ink-secondary"
              title={row.tooltip}
            >
              <ZoneMarker kind={row.marker} size={7} className="shrink-0 text-ink-secondary" />
              <span className="min-w-0">
                {row.label} · {row.text}
              </span>
            </p>
          ))}
          {ledger.sevenDayRow ? (
            <p className="border-b border-[var(--color-divider)] py-[5px] text-ink-secondary" title={ledger.sevenDayRow.tooltip}>
              {ledger.sevenDayRow.label} · {ledger.sevenDayRow.text}
            </p>
          ) : null}
          <p className="mt-1.5 text-[13px] font-semibold text-ink-primary" data-operating-record-sentence>
            {curve.sentence.primary}
          </p>
        </div>
      </GmCard>
    );
  }

  return (
    <SurfacePagePanel
      title={isMet ? 'Operating point' : 'Operating point — grade vs recovery'}
      subtitle={isMet ? 'grade vs recovery' : undefined}
      className={isMet ? '[&_.hairline-b]:py-2' : undefined}
      contentClassName={isMet ? 'py-2' : undefined}
      meta={
        <div role="tablist" aria-label="Concentrate metal" className="flex flex-wrap items-center gap-1">
          {ledger.tabItems.map((item, index) => {
            const active = item.id === metal;
            return (
              <button
                key={item.id}
                id={tabId(item.id)}
                type="button"
                role="tab"
                aria-selected={active}
                aria-controls={chartPanelId}
                tabIndex={active ? 0 : -1}
                onClick={() => setMetal(item.id)}
                onKeyDown={(event) => onTabKeyDown(event, index)}
                className={cn(
                  'inline-flex items-center gap-1.5',
                  active ? SURFACE_PAGE_TAB_ACTIVE : SURFACE_PAGE_TAB_INACTIVE,
                )}
              >
                {item.label}
                {item.hasFuture ? (
                  <span
                    className="inline-block h-[5px] w-[5px] rounded-full"
                    style={{ backgroundColor: vizToneColor(item.tone) }}
                    aria-hidden
                  />
                ) : null}
              </button>
            );
          })}
        </div>
      }
    >
      {/* Met: the key sits beside the plot, not under it — four points do
          not need the panel's full width, and the key reads with the chart.
          The key track is as wide as its longest row, and rows never wrap,
          so no row breaks into an orphaned word; the plot takes the rest. */}
      <div
        className={cn(
          'grid grid-cols-1 gap-3',
          isMet && 'lg:grid-cols-[minmax(0,1fr)_max-content] lg:items-center lg:gap-6',
        )}
      >
        <div
          id={chartPanelId}
          role="tabpanel"
          aria-labelledby={tabId(metal)}
          className="min-w-0"
        >
          <OperatingCurve
            xLabel={curve.xLabel}
            yLabel={curve.yLabel}
            xUnit={curve.xUnit}
            yUnit={curve.yUnit}
            design={curve.design}
            trail={curve.trail}
            current={curve.current}
            future={curve.future}
            tone={curve.tone}
            height={isMet ? 170 : 220}
            cursorTooltip={curve.current.label}
            xDomain={curve.xDomain}
            yDomain={curve.yDomain}
          />
        </div>
        <div
          className={cn(
            'flex min-w-0 flex-col text-sm',
            isMet
              ? 'gap-1 lg:whitespace-nowrap lg:border-l lg:border-[var(--hairline)] lg:pl-5'
              : 'gap-1.5',
          )}
        >
          <p className="tabular-nums text-[var(--ink-primary)]" title={ledger.designRow.tooltip}>
            {isMet ? <LedgerLine row={ledger.designRow} /> : ledgerLine(ledger.designRow)}
          </p>
          <p className="tabular-nums text-[var(--ink-primary)]" title={ledger.nowRow.tooltip}>
            {isMet ? <LedgerLine row={ledger.nowRow} /> : ledgerLine(ledger.nowRow)}
          </p>
          {ledger.zoneRows.map((row) => (
            <p
              key={row.label}
              className="inline-flex items-center gap-1.5 tabular-nums text-[var(--ink-secondary)]"
              title={row.tooltip}
            >
              <ZoneMarker kind={row.marker} size={8} className="text-[var(--ink-tertiary)]" />
              <span>{row.label} · {row.text}</span>
            </p>
          ))}
          {ledger.sevenDayRow ? (
            <p
              className="tabular-nums text-[var(--ink-secondary)]"
              title={ledger.sevenDayRow.tooltip}
            >
              {ledger.sevenDayRow.label} · {ledger.sevenDayRow.text}
            </p>
          ) : null}
          {ledger.futureRow ? (
            <p className="tabular-nums text-[var(--ink-secondary)]" title={ledger.futureRow.tooltip}>
              {ledgerLine(ledger.futureRow)}
            </p>
          ) : null}
          <p
            className={cn(
              'text-sm text-[var(--ink-primary)]',
              isMet && 'mt-1 border-t border-[var(--hairline)] pt-2 font-medium',
            )}
          >
            {curve.sentence.primary}
          </p>
        </div>
      </div>
    </SurfacePagePanel>
  );
}

/** Met key row: the row name in secondary ink, the pair in primary — the
 * same `label · grade · recovery` text, weighted so the pair reads first. */
function LedgerLine({ row }: { row: { label: string; grade: string; recovery: string } }) {
  return (
    <>
      <span className="text-[var(--ink-secondary)]">{row.label}</span>
      {' · '}
      <span className="font-medium">{row.grade}</span>
      {' · '}
      <span className="font-medium">{row.recovery}</span>
    </>
  );
}
